import React, { memo } from 'react';
import {
  Lightbulb,
  Bookmark,
  Clock,
  CalendarDays,
  MapPinned,
  ArrowRight
} from 'lucide-react';
import { ITrip } from '../../../../Models/trips/trip.model';
import { formatDate } from '../../../../utils/date-utils';
import {
  getBookedActivityIds,
  getItineraryActivityIds
} from '../../../../utils/itinerary-activity-utils';
import './overview-panels.scss';

interface TripIdeasItinerariesOverviewProps {
  trip: ITrip;
}

const TripIdeasItinerariesOverview: React.FC<
  TripIdeasItinerariesOverviewProps
> = ({ trip }) => {
  // IDEAS SECTION LOGIC
  const unbookedActivities =
    trip.activities?.filter((activity) => !activity.deleted) || [];

  const totalActivities = trip.totalActivities || 0;
  const bookedActivities = getBookedActivityIds(trip.itineraries).size;

  const ideasCount = Math.max(0, totalActivities - bookedActivities);

  // Group activities by type
  const activityTypes = unbookedActivities.reduce(
    (acc, activity) => {
      const type = activity.type || 'Other';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  const topTypes = Object.entries(activityTypes)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 2);

  const recentActivities = unbookedActivities
    .sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateB - dateA;
    })
    .slice(0, 2);

  // ITINERARIES SECTION LOGIC
  const itineraries =
    trip.itineraries?.filter((itinerary) => !itinerary.deleted) || [];
  const itineraryCount = itineraries.length;

  const totalActivitiesInItineraries = itineraries.reduce((sum, itinerary) => {
    return sum + getItineraryActivityIds(itinerary).length;
  }, 0);

  const totalDays = itineraries.reduce((sum, itinerary) => {
    return sum + (itinerary.days?.length || 0);
  }, 0);

  // Get upcoming itinerary (earliest start date in the future, or most recent if all past)
  const upcomingItinerary = itineraries
    .filter((itinerary) => itinerary.startDate)
    .sort((a, b) => {
      const dateA = new Date(a.startDate!).getTime();
      const dateB = new Date(b.startDate!).getTime();
      return dateA - dateB;
    })[0];

  return (
    <div className='overview-panel-content'>
      <h4 className='panel-title font-libre'>Trip Ideas & Itineraries</h4>

      <div className='panel-stats split-layout'>
        {/* IDEAS SECTION - LEFT SIDE */}
        <div className='split-section'>
          <div className='split-section-header'>
            <Lightbulb size={16} />
            <span>Ideas</span>
          </div>

          <div className='stat-item highlight small'>
            <Lightbulb
              size={18}
              className='stat-icon'
            />
            <div className='stat-info'>
              <div className='stat-label'>Unbooked</div>
              <div className='stat-value large'>{ideasCount}</div>
            </div>
          </div>

          {topTypes.length > 0 && (
            <div className='stat-section compact'>
              <div className='section-title small'>
                <Bookmark size={14} />
                <span>Top Categories</span>
              </div>
              <div className='category-list'>
                {topTypes.map(([type, count]) => (
                  <div
                    key={type}
                    className='category-item'
                  >
                    <span className='category-name'>{type}</span>
                    <span className='category-count'>{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {recentActivities.length > 0 && (
            <div className='stat-section compact'>
              <div className='section-title small'>
                <Clock size={14} />
                <span>Recent</span>
              </div>
              <div className='activity-list'>
                {recentActivities.map((activity) => (
                  <div
                    key={activity.id}
                    className='activity-item'
                  >
                    {activity.title}
                  </div>
                ))}
              </div>
            </div>
          )}

          {ideasCount === 0 && topTypes.length === 0 && (
            <div className='empty-state compact'>
              <p>No ideas yet</p>
            </div>
          )}
        </div>

        {/* DIVIDER */}
        <div className='split-divider'></div>

        {/* ITINERARIES SECTION - RIGHT SIDE */}
        <div className='split-section'>
          <div className='split-section-header'>
            <CalendarDays size={16} />
            <span>Itineraries</span>
          </div>

          <div className='stat-item highlight small'>
            <CalendarDays
              size={18}
              className='stat-icon'
            />
            <div className='stat-info'>
              <div className='stat-label'>Created</div>
              <div className='stat-value large'>{itineraryCount}</div>
            </div>
          </div>

          <div className='stat-grid compact'>
            <div className='stat-item-small'>
              <div className='stat-label'>Days</div>
              <div className='stat-value'>{totalDays}</div>
            </div>
            <div className='stat-item-small'>
              <div className='stat-label'>Activities</div>
              <div className='stat-value'>{totalActivitiesInItineraries}</div>
            </div>
          </div>

          {upcomingItinerary && (
            <div className='stat-section compact'>
              <div className='section-title small'>
                <MapPinned size={14} />
                <span>Next</span>
              </div>
              <div className='upcoming-itinerary compact'>
                <div className='itinerary-name'>{upcomingItinerary.title}</div>
                {upcomingItinerary.startDate && (
                  <div className='itinerary-dates'>
                    {formatDate(new Date(upcomingItinerary.startDate))}
                    {upcomingItinerary.endDate && (
                      <>
                        <ArrowRight size={12} />
                        {formatDate(new Date(upcomingItinerary.endDate))}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {itineraryCount === 0 && (
            <div className='empty-state compact'>
              <p>No itineraries</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(TripIdeasItinerariesOverview);
